import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { cn } from '../../utils/cn'
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  Shield,
  UserCircle,
  X,
  ChevronRight,
  FileText,
  Star,
  BarChart3,
  Plug,
  Database,
  Palette
} from 'lucide-react'
import enginuoLogo from '../../assets/images/enginuo-logo.png'
import { useHubBranding } from '../../hooks/useHubBranding'

export default function Sidebar({ isOpen, onClose }) {
  const { user, hasPermission, isControlCenter } = useAuth()
  const location = useLocation()
  const { branding } = useHubBranding()
  const logoSrc = branding?.logo_url || enginuoLogo
  const appName = branding?.app_name || 'Enginuo'

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      permission: null // Always visible
    },
    {
      name: 'Invoice',
      href: '/invoice',
      icon: FileText,
      permission: 'invoice.read' // Check permission from role_permissions
    },
    {
      name: 'Organizations',
      href: '/organizations',
      icon: Building2,
      permission: 'organizations.read',
      showFor: ['control_center', 'hub_user']
    },
    {
      name: 'Products',
      href: '/products',
      icon: Package,
      permission: 'products.read',
      showFor: ['control_center', 'hub_user']
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: BarChart3,
      permission: 'reports.read' // Check permission from role_permissions
    },
    {
      name: 'Hub Users',
      href: '/sas-users',
      icon: Users,
      permission: 'users.read', // Check permission from role_permissions
      showFor: ['control_center', 'hub_user']
    },
    {
      name: 'Hub Roles',
      href: '/roles',
      icon: Shield,
      permission: 'roles.read',
      showFor: ['control_center', 'hub_user']
    },
    {
      name: 'Hub Feature',
      href: '/hub-features',
      icon: Star,
      permission: 'features.read' // Check permission from role_permissions
    },
    {
      name: 'Integration',
      href: '/integration',
      icon: Plug,
      permission: 'integration.read' // Check permission from role_permissions
    },
    {
      name: 'Branding',
      href: '/branding',
      icon: Palette,
      permission: 'integration.read',
      showFor: ['control_center', 'hub_user']
    },
    {
      name: 'Data Backup',
      href: '/data-backup',
      icon: Database,
      permission: 'reports.read' // Check permission from role_permissions
    }
  ]

  const filteredNavigation = navigation.filter(item => {
    // Items without permission requirement are always visible
    if (!item.permission) return true
    
    // Check if item should be shown for this user type
    if (item.showFor && !item.showFor.includes(user?.user_type)) return false
    
    // Check permissions from role_permissions in database
    // The hasPermission function checks the user's role_permissions object
    // which comes from the product_roles or roles table permissions field
    return hasPermission(item.permission)
  })

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-secondary-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:min-h-screen',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-center px-6 border-b border-secondary-200 relative">
            <div className="flex items-center justify-center">
              <img 
                src={logoSrc} 
                alt={appName} 
                className="h-12 w-auto max-w-[180px] object-contain"
              />
              {/* <span className="ml-3 text-lg font-semibold text-secondary-900">
                enginuo
              </span> */}
            </div>
            <button
              onClick={onClose}
              className="lg:hidden absolute right-4 p-1 rounded-md hover:bg-secondary-100"
            >
              <X className="h-5 w-5 text-secondary-500" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-secondary-200">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <UserCircle className="h-5 w-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-secondary-900">
                  {user?.full_name || user?.username}
                </p>
                <p className="text-xs text-secondary-500">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item, index) => {
              const isActive = location.pathname === item.href
              const isReports = item.name === 'Reports'
              return (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      'nav-item',
                      isActive && 'nav-item-active',
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors'
                    )}
                    onClick={onClose}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                    {isActive && (
                      <ChevronRight className="ml-auto h-4 w-4" />
                    )}
                  </Link>
                  {isReports && (
                    <div className="my-2 border-t border-secondary-200"></div>
                  )}
                </div>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="border-t border-secondary-200 p-4">
          </div>
        </div>
      </div>
    </>
  )
}
